import { hasOnlyIncompleteTariffs } from '@billing/validation/incompleteClaim';
//React
import React, {
  Fragment,
  ReactElement,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react';

//Sentry
import * as Sentry from '@sentry/react';

//Mantine
import { Alert, LoadingOverlay, Text } from '@mantine/core';
import { MRT_Row, MRT_TableInstance } from 'mantine-react-table';
import { useTranslation } from '@refinedev/core';

//Interfaces
import { Claim, Payor, Provider } from 'interfaces';

//Components
import {
  AllKeys,
  useActiveForm,
  BulkClaimPreviewSection,
  IndividualClaimSection,
  ClaimFormProvider,
  bulkInfoSectionKeys,
  bulkDetailSectionKeys,
  bulkSharedDetailSectionKeys,
  patientSectionKeys as patientKeys,
  healthcardSectionKeys as healthcardKeys,
  providerSectionKeys as providerKeys,
  tariffSectionKeys as tariffKeys,
  baseAdditionalInfoSectionKeys,
  individualDetailSectionKeys,
  claimCommentSectionKeys,
  FutureServiceDateAlert,
} from 'pages/claims/components';

import {
  useClaimDraftStore,
  useCurrentUserStore,
  useCurrentSiteStore,
} from 'store';
import { stripNested } from 'utils/miscUtils';
import { getValueByPath } from 'utils/getValueByPath';
import { mergeTariffs } from 'utils/tariffUtils';
import { nowISO } from 'utils/dateUtils';
import { ClaimTariff } from '@billing/validation';
import type { LiveBulkClaimRulesState } from 'hooks/useLiveBulkClaimRules';
import { useApplyLiveRuleData } from 'hooks/useApplyLiveRuleData';
import { useClaimRuleValidation } from 'hooks/useClaimRuleValidation';
import { useValidationSummaryFeedback } from 'hooks/useValidationSummaryFeedback';

interface BulkCreateClaimModalProps {
  table: MRT_TableInstance<Claim>;
  row: MRT_Row<Claim>;
  internalEditComponents?: React.ReactNode[];
  providers: Provider[];
  payors: Payor[];
  liveRules: LiveBulkClaimRulesState;
}

/*
 * - Step -1: Collect shared data & selected patientIDs
 * - Steps 0..N-1: For each patient, instantiate an individual claim form
 * - Step N: Preview all claims
 */
export const BulkCreateClaimModal: React.FC<BulkCreateClaimModalProps> = ({
  table,
  row,
  internalEditComponents,
  providers,
  payors,
  liveRules,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showErrors, setShowErrors] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<number>(-1);
  const [submitClaims, setSubmitClaims] = useState<boolean>(false);
  const [editingFromPreview, setEditingFromPreview] = useState<boolean>(false);
  const [returnToClaimIndex, setReturnToClaimIndex] = useState<number | null>(
    null
  );
  const { id: currentUserID } = useCurrentUserStore();
  const { timezone } = useCurrentSiteStore();
  const { translate } = useTranslation();
  const { form } = useActiveForm();
  const currentClaimValues = form.getValues().claims || [];
  const allClaimsIncomplete =
    currentClaimValues.length > 0 &&
    currentClaimValues.every(hasOnlyIncompleteTariffs);
  const { clearBulkDraft, getBulkDraftFull, saveBulkDraftFromRows } =
    useClaimDraftStore();
  const { revealValidationSummary, validationSummaryRef } =
    useValidationSummaryFeedback();
  const {
    clearServerRuleValidation,
    hasCurrentBlockingRules,
    liveBlockingRuleMessages,
    liveRuleError,
    liveRuleWarningMessages,
    isEvaluating,
    serverRuleErrors,
    setServerRuleFailure,
  } = useClaimRuleValidation(form, liveRules, allClaimsIncomplete);
  useApplyLiveRuleData(form, liveRules);

  useEffect(() => {
    if (showErrors) {
      revealValidationSummary();
    }
  }, [revealValidationSummary, showErrors]);

  // On mount, if there is a stored draft and existing claims, set currentStep to last claim
  useEffect(() => {
    const draft = getBulkDraftFull();
    const existing = form.getValues().claims || [];
    if (draft && existing.length > 0) {
      setCurrentStep(existing.length - 1);
    } else {
      setCurrentStep(-1);
    }
    // Instrumentation
    Sentry.captureMessage('[BULK_AUDIT]Init Bulk Claim Modal', {
      level: 'info',
      extra: {
        formType: 'bulk',
        draftClaimCount: draft?.data?.length || 0,
        existingClaimCount: existing.length,
        draftClaims: draft?.data?.map((c) => stripNested(c)),
        existingClaims: existing?.map((c) => stripNested(c)),
        user: currentUserID,
      },
    });
  }, []);

  const claims = form?.getValues()?.claims || [];
  const totalClaims = form.getValues()?.claims?.length || 0;
  const activePrefix = currentStep === -1 ? 'shared' : `claims.${currentStep}`;

  form.watch(`${activePrefix}.payorID`, ({ previousValue, value }) => {
    if (previousValue === value) {
      return;
    }

    if (!value) {
      form.setFieldValue(`${activePrefix}.payor`, null);
      return;
    }

    const payor = payors.find((payor) => payor.id === value);
    if (payor) {
      form.setFieldValue(`${activePrefix}.payor`, payor);
    }
  });

  const editComponentsMap = useMemo(() => {
    const map: Record<string, ReactNode> = {};
    internalEditComponents.forEach((comp: ReactElement) => {
      if (comp.key) map[comp.key as string] = comp;
    });
    return map;
  }, [internalEditComponents]);

  const infoSectionKeys: AllKeys[] = useMemo(() => bulkInfoSectionKeys, []);
  const detailSectionKeys: AllKeys[] = useMemo(() => bulkDetailSectionKeys, []);
  const sharedDetailSectionKeys: AllKeys[] = useMemo(
    () => bulkSharedDetailSectionKeys,
    []
  );
  const additionalInfoKeys: AllKeys[] = useMemo(
    () => baseAdditionalInfoSectionKeys,
    []
  );
  const providerSectionKeys: AllKeys[] = useMemo(() => providerKeys, []);
  const patientSectionKeys: AllKeys[] = useMemo(() => patientKeys, []);
  const healthcardSectionKeys: AllKeys[] = useMemo(() => healthcardKeys, []);
  const tariffSectionKeys: AllKeys[] = useMemo(() => tariffKeys, []);

  const handleCancel = () => {
    clearBulkDraft();
    table.setCreatingRow(null);
  };

  // Helper to set nested value by path
  const setByPath = (obj: any, path: string, val: any) => {
    const keys = path.split('.');
    const last = keys.pop() as string;
    const target = keys.reduce((acc, key) => {
      if (!acc[key] || typeof acc[key] !== 'object') acc[key] = {};
      return acc[key];
    }, obj);
    target[last] = val;
    return obj;
  };

  // All paths that can be shared & merged from "shared" into individual claims
  const sharedMergePaths = useMemo(
    () => [
      ...providerSectionKeys,
      ...detailSectionKeys,
      ...claimCommentSectionKeys,
      ...additionalInfoKeys,
      ...healthcardSectionKeys,
      ...tariffSectionKeys,
      'payor',
      'intakeItemID',
    ],
    [
      providerSectionKeys,
      detailSectionKeys,
      additionalInfoKeys,
      healthcardSectionKeys,
      tariffSectionKeys,
    ]
  );

  // prevents typescript warning about ClaimTariff vs Tariff type mismatch when merging tariffs
  const normalizeTariffsForMerge = (tariffs: Claim['tariffs']) =>
    (tariffs || []).map((tariff: ClaimTariff) => ({
      ...tariff,
      specialtyCode: tariff?.specialtyCode ?? '',
    }));

  // Merge shared data into an individual claim, without overwriting  existing claim values
  const mergeSharedIntoClaim = (shared: Claim, claim: Claim): Claim => {
    sharedMergePaths.forEach((path) => {
      // Tariffs handled separately
      if (path === 'tariffs') return;
      const sharedVal = getValueByPath(shared, path);
      const claimVal = getValueByPath(claim, path);
      if (claimVal === null || claimVal === undefined || claimVal === '') {
        // only set if claim value is empty (null/undefined/empty string)
        setByPath(claim, path, sharedVal);
      }
    });
    if (shared.icd9) claim.icd9 = shared.icd9;
    // Tariffs: always merge via helper
    claim.tariffs = mergeTariffs(
      normalizeTariffsForMerge(shared.tariffs),
      normalizeTariffsForMerge(claim?.tariffs),
      shared.icd9 ?? undefined
    ) as Array<ClaimTariff>;
    return claim;
  };

  const createClaimFromShared = (shared: Claim): Claim => {
    const sharedClaim = JSON.parse(JSON.stringify(shared)) as Claim;

    return {
      ...sharedClaim,
      tariffs: mergeTariffs(
        normalizeTariffsForMerge(sharedClaim.tariffs),
        [],
        sharedClaim.icd9 ?? undefined
      ) as Array<ClaimTariff>,
    };
  };

  const handleSharedNext = () => {
    if (!form.isValid('shared')) {
      form.validate();
      setShowErrors(true);
      return;
    }
    setShowErrors(false);
    const values = form.getValues();
    const { shared } = values;
    const existingClaims = values.claims || [];
    // If there are existing claims, merge shared values into each claim
    if (existingClaims.length > 0) {
      const merged = existingClaims.map((claim: Claim) =>
        mergeSharedIntoClaim(shared, claim)
      );
      form.setFieldValue('claims', merged);
      Sentry.captureMessage('[BULK_AUDIT]Shared Data Edited & Merged', {
        level: 'info',
        extra: {
          formType: 'bulk',
          beforeCount: existingClaims.length,
          afterCount: merged.length,
          mergedClaims: merged.map((c: any) => stripNested(c)),
          user: currentUserID,
        },
      });
      saveBulkDraftFromRows(shared, merged); // save merged state as form draft
      setCurrentStep(returnToClaimIndex ?? 0); // navigate to first claim or return index
      setReturnToClaimIndex(null); // clear return index after navigation
    } else {
      // First time bulk creation: seed first claim from shared data
      form.setValues({
        ...values,
        claims: [
          {
            provider: null,
            providerID: null,
            ...createClaimFromShared(shared),
            isNewPatient: false,
            createdAt: nowISO(),
          },
        ],
      });
      Sentry.captureMessage('[BULK_AUDIT]Initial Shared Data Seed', {
        level: 'info',
        extra: {
          formType: 'bulk',
          claimCount: 1,
          shared: stripNested(shared as any),
          user: currentUserID,
        },
      });
      setCurrentStep(0);
    }
  };

  // Merge shared data into existing claims WITHOUT altering currentStep (used when user clicks directly on a claim step)
  const handleStepperNavigation = () => {
    const values = form.getValues();
    const { shared } = values;
    const existingClaims = values.claims || [];
    if (existingClaims.length === 0) return; // nothing to merge yet
    const merged = existingClaims.map((claim: Claim) =>
      mergeSharedIntoClaim(shared, claim)
    );
    form.setFieldValue('claims', merged);
    Sentry.captureMessage(
      '[BULK_AUDIT]Shared Data Edited & Merged (Silent Navigation)',
      {
        level: 'info',
        extra: {
          formType: 'bulk',
          beforeCount: existingClaims.length,
          afterCount: merged.length,
          mergedClaims: merged.map((c: any) => stripNested(c)),
          user: currentUserID,
        },
      }
    );
    saveBulkDraftFromRows(shared, merged as any);
  };

  const handlePatientNext = () => {
    setShowErrors(false);
    const currentClaims = form.getValues().claims || [];
    // Append a new blank only if: not previewing, we're at the last claim, and the last claim passes minimal completeness.
    if (!submitClaims && currentStep === totalClaims - 1) {
      const sharedData = form.getValues().shared;
      const newClaim = {
        ...createClaimFromShared(sharedData),
        createdAt: nowISO(),
        patient: null,
        patientID: null,
        healthcard: null,
        healthcardID: null,
      };
      form.setFieldValue('claims', [...currentClaims, newClaim]);
      Sentry.captureMessage('[BULK_AUDIT]Setting Local Batch Form Data', {
        level: 'info',
        extra: {
          formType: 'bulk',
          count: [...currentClaims, newClaim].length,
          claims: [...currentClaims, newClaim].map((c) => stripNested(c)),
          user: currentUserID,
        },
      });
    }
    setCurrentStep((prev) => prev + 1);
  };

  const handlePatientPrev = () => {
    setShowErrors(false);
    setCurrentStep((prev) => prev - 1);
  };

  const handleSubmitAll = async () => {
    const currentClaims = form.getValues().claims || [];
    const allIncomplete =
      currentClaims.length > 0 && currentClaims.every(hasOnlyIncompleteTariffs);
    if (!allIncomplete && (isEvaluating || hasCurrentBlockingRules)) {
      setShowErrors(true);
      return;
    }

    const validationResult = form.validate();
    if (validationResult.hasErrors) {
      setShowErrors(true);
      return;
    }

    setIsLoading(true);
    setShowErrors(false);
    clearServerRuleValidation();
    try {
      // Snapshot claims to avoid mutation during async submit
      const outgoingClaims = JSON.parse(
        JSON.stringify(form.getValues().claims || [])
      );
      // Instrumentation: hash each claim to detect duplicates
      const sentryData = [];
      outgoingClaims.forEach((c: any) => {
        sentryData.push(stripNested(c));
      });
      Sentry.captureMessage('[BULK_AUDIT]Submitting Batch Data', {
        level: 'info',
        extra: {
          formType: 'bulk',
          count: outgoingClaims.length,
          claims: sentryData,
          user: currentUserID,
        },
      });
      await table.options.onCreatingRowSave?.({
        exitCreatingMode: () => table.setCreatingRow(null),
        row,
        table,
        values: outgoingClaims as any,
      });
      clearBulkDraft();
    } catch (error) {
      setServerRuleFailure(error);
      Sentry.captureException(error);
      setShowErrors(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (form.isValid('shared')) {
      setShowErrors(false);
    }
  }, [currentStep, form.getValues().shared]);

  return (
    <Fragment>
      <LoadingOverlay visible={isLoading} />
      <div
        ref={validationSummaryRef}
        data-testid="claim-validation-summary"
        tabIndex={-1}
        style={{ scrollMarginTop: 'var(--mantine-spacing-md)' }}
      />
      <FutureServiceDateAlert
        serviceStarts={claims.map((claim) => claim.serviceStart)}
        timezone={timezone}
      />
      {serverRuleErrors.length > 0 && (
        <Alert
          title={translate(
            'pages:claims.modals.ruleValidationFailedTitle',
            'Rule validation failed'
          )}
          color="red"
          radius="sm"
          mb="md"
        >
          {serverRuleErrors.map((message) => (
            <Text key={message}>{message}</Text>
          ))}
        </Alert>
      )}
      {liveBlockingRuleMessages.length > 0 && serverRuleErrors.length === 0 && (
        <Alert
          title={translate(
            'pages:claims.modals.ruleValidationFailedTitle',
            'Rule validation failed'
          )}
          color="red"
          radius="sm"
          mb="md"
        >
          {liveBlockingRuleMessages.map((message) => (
            <Text key={message}>{message}</Text>
          ))}
        </Alert>
      )}
      {liveRuleWarningMessages.length > 0 && (
        <Alert
          title={translate(
            'pages:claims.modals.ruleWarningsTitle',
            'Billing rule warnings'
          )}
          color="yellow"
          radius="sm"
          mb="md"
        >
          {liveRuleWarningMessages.map((message) => (
            <Text key={message}>{message}</Text>
          ))}
        </Alert>
      )}
      <Text
        size="sm"
        c="dimmed"
        ta="center"
        mb="md"
        aria-hidden={!isEvaluating}
      >
        {isEvaluating
          ? translate(
              'pages:claims.modals.rulesChecking',
              'Checking billing rules…'
            )
          : '\u00a0'}
      </Text>
      {liveRuleError && !isEvaluating && (
        <Alert
          title={translate(
            'pages:claims.modals.rulesUnavailableTitle',
            'Rule preview unavailable'
          )}
          color="yellow"
          radius="sm"
          mb="md"
        >
          {translate(
            'pages:claims.modals.rulesUnavailableMessage',
            'Rules will be checked again when you save.'
          )}
        </Alert>
      )}
      {showErrors &&
        currentStep < 0 &&
        Object.keys(form?.errors).length > 0 && (
          <Alert title="Form is invalid" color="red" radius="sm" mb="md">
            {Object?.keys(form?.errors).map((key) => (
              <Text key={key}>{`${key}: ${form.errors[key]}`}</Text>
            ))}
          </Alert>
        )}
      {/* Unified stepper: shared step + claim steps */}
      {!submitClaims && (
        <ClaimFormProvider
          key={currentStep}
          form={{
            prefix: currentStep === -1 ? 'shared' : `claims.${currentStep}`,
            formType: currentStep === -1 ? 'bulk' : 'individual',
            bulkForm: form,
          }}
        >
          <IndividualClaimSection
            table={table}
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
            editComponentsMap={editComponentsMap}
            bulkForm={form}
            infoSectionKeys={infoSectionKeys}
            detailSectionKeys={individualDetailSectionKeys}
            patientSectionKeys={patientSectionKeys}
            providerSectionKeys={providerSectionKeys}
            providers={providers}
            tariffSectionKeys={tariffSectionKeys}
            healthcardSectionKeys={healthcardSectionKeys}
            handlePatientPrev={handlePatientPrev}
            handlePatientNext={handlePatientNext}
            handleSharedNext={handleSharedNext}
            setSubmitClaims={setSubmitClaims}
            editingFromPreview={editingFromPreview}
            onFinishEditFromPreview={() => {
              setSubmitClaims(true);
              setEditingFromPreview(false);
            }}
            showErrors={showErrors}
            setShowErrors={setShowErrors}
            onEditShared={() => {
              setReturnToClaimIndex(currentStep);
              setCurrentStep(-1);
            }}
            onCancel={handleCancel}
            sharedIndex={row.index}
            sharedDetailSectionKeys={sharedDetailSectionKeys}
            additionalInfoKeys={additionalInfoKeys}
            sharedTariffSectionKeys={tariffSectionKeys}
            totalClaims={totalClaims}
            onStepperNavigation={handleStepperNavigation}
            validateSharedBeforeNavigation={() => {
              if (!form.isValid('shared')) {
                form.validate();
                setShowErrors(true);
                return false;
              }
              setShowErrors(false);
              return true;
            }}
          />
        </ClaimFormProvider>
      )}
      {/* Step N+1: Preview all claims */}
      {submitClaims && (
        <BulkClaimPreviewSection
          handlePatientPrev={handlePatientPrev}
          handleSubmitAll={handleSubmitAll}
          payors={payors}
          isLoading={isLoading}
          claims={claims}
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
          setSubmitClaims={setSubmitClaims}
          setEditingFromPreview={setEditingFromPreview}
          setShowErrors={setShowErrors}
          isEvaluating={isEvaluating}
          hasCurrentBlockingRules={hasCurrentBlockingRules}
        />
      )}
    </Fragment>
  );
};
